/**
 * Clean V2 API Client - No V1 compatibility, pure V2 implementation
 */
class V2ApiClient {
  constructor(token) {
    this.token = token;
    this.baseUrl = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
    this.abortController = null;
  }

  /**
   * Set AbortController for cancelling in-flight requests
   */
  setAbortController(controller) {
    this.abortController = controller;
  }

  /**
   * Get the signal for fetch requests
   */
  getSignal() {
    return this.abortController?.signal;
  }

  /**
   * Start a new V2 workflow execution
   */
  async startWorkflow(workflowId, userMessage) {
    console.log('🚀 V2API: Starting workflow', { workflowId, userMessage });

    const response = await fetch(`${this.baseUrl}/api/v2/workflows/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      },
      body: JSON.stringify({
        workflow_id: parseInt(workflowId),
        user_message: userMessage,
        token: this.token
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to start workflow');
    }

    console.log('✅ V2API: Workflow started', data);
    return data;
  }

  /**
   * Continue an existing V2 workflow execution
   */
  async continueWorkflow(runId, userMessage) {
    console.log('🔄 V2API: Continuing workflow', { runId, userMessage });

    const response = await fetch(`${this.baseUrl}/api/v2/workflows/continue/${runId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      },
      body: JSON.stringify({
        user_message: userMessage,
        token: this.token
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to continue workflow');
    }

    console.log('✅ V2API: Workflow continued', data);
    return data;
  }

  /**
   * Get workflow execution status and details
   */
  async getWorkflowStatus(runId) {
    console.log('📊 V2API: Getting workflow status', { runId });

    const response = await fetch(`${this.baseUrl}/api/v2/workflows/status/${runId}`, {
      headers: {
        'Authorization': `Bearer ${this.token}`
      },
      signal: this.getSignal()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    console.log('✅ V2API: Got workflow status', data);
    return data;
  }

  /**
   * Get detailed execution information for debugging
   */
  async getExecutionDetails(runId) {
    console.log('🔍 V2API: Getting execution details', { runId });

    const response = await fetch(`${this.baseUrl}/api/v2/workflows/${runId}/execution-details`, {
      headers: {
        'Authorization': `Bearer ${this.token}`
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    console.log('✅ V2API: Got execution details', data);
    return data;
  }

  /**
   * Get workflow events and chat messages
   */
  async getWorkflowEvents(runId, since = null) {
    console.log('📞 V2API: Getting workflow events', { runId, since });

    let url = `${this.baseUrl}/api/v2/workflows/${runId}/events`;
    if (since) {
      url += `?since=${encodeURIComponent(since)}`;
    }

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${this.token}`
      },
      signal: this.getSignal()
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    console.log('✅ V2API: Got workflow events', data);
    return data;
  }

  /**
   * Health check for V2 API
   */
  async healthCheck() {
    const response = await fetch(`${this.baseUrl}/api/v2/workflows/health`);
    return response.json();
  }

}

export default V2ApiClient;
